import { css } from 'lit';

export const analyticsViewStyles = css`
  :host {
    display: block;
  }

  .back-btn {
    background: none;
    border: none;
    color: var(--accent-color, #245590);
    font-family: var(--font-mono, monospace);
    font-size: 0.9rem;
    cursor: pointer;
    padding: 0;
    margin-bottom: 1.5rem;
    display: inline-flex;
    align-items: center;
    gap: 0.3rem;
  }

  .back-btn:hover {
    text-decoration: underline;
  }

  h1 {
    font-family: var(--font-serif, serif);
    font-size: 2.1rem;
    font-weight: 700;
    line-height: 1.25;
    color: var(--text-primary, #1a1a18);
    margin-bottom: 2rem;
  }

  h2 {
    font-family: var(--font-serif, serif);
    font-size: 1.5rem;
    font-weight: 700;
    line-height: 1.25;
    color: var(--text-primary, #1a1a18);
    margin: 2rem 0 1rem;
  }

  /* Full-Page Password Modal Overlay */
  .password-modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    width: 100vw;
    height: 100vh;
    background: var(--bg-primary, #fafaf7);
    z-index: 99999;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 1.5rem;
    box-sizing: border-box;
  }

  .password-modal-card {
    background: var(--bg-secondary, #f6f6f2);
    border: 1px solid var(--border-color, #e5e5de);
    border-radius: 8px;
    padding: 1.4rem 1.6rem 1.6rem 1.6rem;
    width: 100%;
    max-width: 440px;
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.04);
    box-sizing: border-box;
  }

  .modal-top-bar {
    margin-top: 0;
    margin-bottom: 0.75rem;
    margin-left: -0.2rem;
    display: flex;
    align-items: center;
  }

  .modal-title {
    font-family: var(--font-serif, serif);
    font-size: 1.5rem;
    font-weight: 700;
    line-height: 1.3;
    color: var(--text-primary, #1a1a18);
    margin: 0 0 0.25rem 0;
  }

  .modal-subtitle {
    font-family: var(--font-mono, monospace);
    font-size: 0.75rem;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: var(--text-muted, #7c7c75);
    margin: 0 0 1.35rem 0;
  }

  .password-form {
    display: flex;
    flex-direction: column;
    gap: 0.6rem;
  }

  .input-row {
    display: flex;
    gap: 0.5rem;
    align-items: center;
    height: 40px;
  }

  .password-input-wrapper {
    flex: 1;
    position: relative;
    display: flex;
    align-items: center;
    height: 40px;
  }

  .password-input-wrapper .password-input {
    width: 100%;
    height: 40px;
    font-family: var(--font-mono, monospace);
    font-size: 0.9rem;
    padding: 0 2.2rem 0 0.85rem;
    border: 1px solid var(--border-color, #e5e5de);
    border-radius: 4px;
    background: var(--bg-primary, #ffffff);
    color: var(--text-primary, #1a1a18);
    outline: none;
    box-sizing: border-box;
    transition: border-color 0.15s ease;
  }

  .password-input-wrapper .password-input:focus {
    border-color: var(--accent-color, #245590);
  }

  .toggle-password-btn {
    position: absolute;
    right: 0.5rem;
    background: none;
    border: none;
    padding: 0.25rem;
    color: var(--text-muted, #7c7c75);
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border-radius: 3px;
    transition: color 0.15s ease;
  }

  .toggle-password-btn:hover {
    color: var(--text-primary, #1a1a18);
  }

  .input-row app-button {
    height: 40px;
    display: inline-flex;
  }

  .error-msg {
    color: #d9383a;
    font-size: 0.82rem;
    font-family: var(--font-mono, monospace);
    margin-top: 0.15rem;
  }

  .loading-container {
    display: flex;
    justify-content: center;
    align-items: center;
    height: 50vh;
    font-family: var(--font-mono, monospace);
    color: var(--text-muted, #7c7c75);
  }

  /* Dashboard Grid Layout */
  .stat-grid {
    display: grid;
    grid-template-columns: repeat(1, 1fr);
    gap: 1rem;
    margin-bottom: 2rem;
  }

  @media (min-width: 768px) {
    .stat-grid {
      grid-template-columns: repeat(3, 1fr);
    }
  }

  .stat-card {
    background: var(--bg-secondary, #f6f6f2);
    border: 1px solid var(--border-color, #e5e5de);
    border-radius: 8px;
    padding: 1.5rem;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
  }

  .stat-value {
    font-family: var(--font-serif, serif);
    font-size: 2.5rem;
    font-weight: 700;
    color: var(--text-primary, #1a1a18);
    line-height: 1.1;
    margin-bottom: 0.5rem;
  }

  .stat-label {
    font-family: var(--font-mono, monospace);
    font-size: 0.85rem;
    color: var(--text-muted, #7c7c75);
    text-transform: uppercase;
    letter-spacing: 0.05em;
  }

  /* Tables */
  .table-wrapper {
    width: 100%;
    overflow-x: auto;
    margin-bottom: 2rem;
  }

  table {
    width: 100%;
    border-collapse: collapse;
    font-family: var(--font-mono, monospace);
    font-size: 0.9rem;
    text-align: left;
  }

  th, td {
    padding: 0.75rem 1rem;
    border-bottom: 1px solid var(--border-color, #e5e5de);
  }

  th {
    color: var(--text-muted, #7c7c75);
    font-weight: normal;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    font-size: 0.8rem;
  }

  tbody tr:nth-child(even) {
    background: var(--bg-secondary, #f6f6f2);
  }

  .bar-container {
    width: 100%;
    height: 8px;
    background: var(--border-color, #e5e5de);
    border-radius: 4px;
    overflow: hidden;
    margin-top: 0.4rem;
  }

  .bar-fill {
    height: 100%;
    background: var(--accent-color, #245590);
  }
`;
